import { useEffect, useState, useCallback } from 'react';
import { getMobileLoanDetail, updateHotFile, createPostHuddleAction } from '../../lib/api';
import { X, Phone, ChatText, NotePencil, Plus, CaretLeft, Siren, Fire, UserCircle, Calendar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const DRAFT_KEY = (id) => `huddle-note-draft-${id}`;

export default function MobileLoanDetail({ fileId, onClose, currentUser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [noteDraft, setNoteDraft] = useState(() => localStorage.getItem(DRAFT_KEY(fileId)) || '');
  const [saving, setSaving] = useState(false);
  const [newTask, setNewTask] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await getMobileLoanDetail(fileId);
      setData(d);
    } catch (e) { toast.error('Could not load file'); }
    finally { setLoading(false); }
  }, [fileId]);
  useEffect(() => { load(); }, [load]);

  // Persist note draft
  useEffect(() => {
    if (noteDraft) localStorage.setItem(DRAFT_KEY(fileId), noteDraft);
    else localStorage.removeItem(DRAFT_KEY(fileId));
  }, [noteDraft, fileId]);

  const handleAddNote = async () => {
    if (!noteDraft.trim() || saving) return;
    setSaving(true);
    try {
      const stamp = `${new Date().toLocaleString()} · ${currentUser?.initials || ''}: ${noteDraft.trim()}`;
      const existing = data.file.notes || '';
      await updateHotFile(data.file.id, { notes: existing ? `${existing}\n${stamp}` : stamp });
      localStorage.removeItem(DRAFT_KEY(fileId));
      setNoteDraft('');
      toast.success('Note added');
      load();
    } catch (e) { toast.error('Could not save note'); }
    finally { setSaving(false); }
  };

  const handleAddTask = async () => {
    if (!newTask.trim() || !data?.file) return;
    try {
      await createPostHuddleAction({ borrower: data.file.borrower, action: newTask.trim(), assigned_to: currentUser?.initials || '', deadline: new Date().toISOString().slice(0,10) });
      setNewTask('');
      toast.success('Task created');
      load();
    } catch (e) { toast.error('Could not create task'); }
  };

  const file = data?.file;
  const contact = data?.assigned_contact;
  const phone = contact?.phone || '';
  const smsBorrower = file?.phone || ''; // if hot_file ever has phone
  const closeDate = file?.closing_date ? new Date(file.closing_date).toLocaleDateString() : null;
  const urgencyCls = file?.urgency === 'Critical' ? 'chip-critical' : file?.urgency === 'High' ? 'chip-warn' : 'chip-neutral';

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col" data-testid="loan-detail" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-border bg-card sticky top-0 z-10">
        <button onClick={onClose} data-testid="loan-close" className="p-1 min-w-[44px] min-h-[44px] flex items-center justify-center text-muted-foreground"><CaretLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-foreground truncate leading-tight">{file?.borrower || 'Loading…'}</p>
          <p className="text-[11px] text-muted-foreground truncate">{file?.sa_group} · {file?.status}</p>
        </div>
      </div>

      {loading || !file ? (
        <div className="p-4 space-y-2">{[0,1,2].map(i => <div key={i} className="h-16 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {/* Hero: stage · close · next action · owner */}
          <div className="p-3 border-b border-border bg-muted/20">
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${urgencyCls}`}>{file.urgency}</span>
              <span className="text-[11px] text-muted-foreground">· {file.status}</span>
              {file.closing_date && <span className="text-[11px] text-muted-foreground">· Closes {closeDate}</span>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="mr-card p-2">
                <p className="text-[10px] uppercase font-bold text-muted-foreground">Next action</p>
                <p className="text-xs font-bold text-foreground leading-snug mt-0.5">{file.action_item || '—'}</p>
              </div>
              <div className="mr-card p-2">
                <p className="text-[10px] uppercase font-bold text-muted-foreground">Owner</p>
                <p className="text-xs font-bold text-foreground leading-snug mt-0.5">{file.assigned_to || file.loan_officer || '—'}</p>
              </div>
            </div>
          </div>

          {/* Action area: Call / Text / Note / Task */}
          <div className="grid grid-cols-4 gap-1 p-2 border-b border-border bg-card">
            <ActionBtn disabled={!phone} href={phone ? `tel:${phone}` : null} icon={Phone} label="Call" testid="action-call" />
            <ActionBtn disabled={!phone} href={phone ? `sms:${phone}?&body=${encodeURIComponent(`Hi — following up on ${file.borrower}'s file.`)}` : null} icon={ChatText} label="Text" testid="action-text" />
            <ActionBtn onClick={() => setTab('notes')} icon={NotePencil} label="Note" testid="action-note" />
            <ActionBtn onClick={() => setTab('tasks')} icon={Plus} label="Task" testid="action-task" />
          </div>
          {!phone && (
            <p className="text-[10px] text-muted-foreground px-3 py-1.5">No phone on file for the assigned team member — add it in Admin to enable Call/Text.</p>
          )}

          {/* Tabs */}
          <div className="flex border-b border-border bg-card sticky top-[64px] z-10">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'tasks',    label: `Tasks (${data.actions?.length || 0})` },
              { id: 'activity', label: `Activity (${(data.escalations?.length||0)+(data.flags?.length||0)})` },
              { id: 'notes',    label: 'Notes' },
            ].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`loan-tab-${t.id}`}
                className={`flex-1 py-2.5 text-[11px] font-bold border-b-2 ${tab === t.id ? 'border-[color:var(--mr-red)] text-[color:var(--mr-red-strong)]' : 'border-transparent text-muted-foreground'}`}>
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-3">
            {tab === 'overview' && (
              <div className="space-y-2 text-sm">
                <Row k="Borrower" v={file.borrower} />
                <Row k="SA Group" v={file.sa_group} />
                <Row k="Status" v={file.status} />
                <Row k="Urgency" v={file.urgency} />
                <Row k="Action item" v={file.action_item} />
                <Row k="Task type" v={file.task_type} />
                <Row k="Closing date" v={closeDate || '—'} />
                <Row k="Loan officer" v={file.loan_officer || '—'} />
                <Row k="Assigned to" v={file.assigned_to || '—'} />
                {file.ai_suggestion && (
                  <div className="mr-card p-2.5 chip-info border mt-2">
                    <p className="text-[10px] uppercase font-bold mb-0.5">Clara's suggestion</p>
                    <p className="text-xs leading-snug">{file.ai_suggestion}</p>
                  </div>
                )}
              </div>
            )}

            {tab === 'tasks' && (
              <div className="space-y-2">
                <div className="flex gap-1.5">
                  <input value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="Add a task…"
                    data-testid="new-task-input" style={{ fontSize: '16px' }}
                    className="flex-1 h-10 px-3 rounded-lg bg-card border border-border text-sm" />
                  <Button onClick={handleAddTask} disabled={!newTask.trim()} className="mr-btn-primary h-10 px-3" data-testid="save-task"><Plus size={16} /></Button>
                </div>
                {data.actions?.length ? data.actions.map(a => (
                  <div key={a.id} className="mr-card p-2.5" data-testid={`task-${a.id}`}>
                    <p className="text-xs font-bold text-foreground">{a.action}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Due {a.deadline || '—'} · {a.assigned_to} · {a.status}</p>
                  </div>
                )) : <p className="text-xs text-muted-foreground text-center py-6">No tasks yet.</p>}
              </div>
            )}

            {tab === 'activity' && (
              <div className="space-y-2">
                {data.escalations?.map(e => (
                  <div key={e.id} className="mr-card p-2.5 border-l-2 border-l-[color:var(--mr-red)]" data-testid={`activity-esc-${e.id}`}>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Siren size={12} className="text-[color:var(--mr-red)]" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--mr-red-strong)]">Escalation</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{new Date(e.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-xs text-foreground">{e.from_member} → {e.to_member}: {e.reason}</p>
                  </div>
                ))}
                {data.flags?.map(f => (
                  <div key={f.id || f.created_at} className="mr-card p-2.5 border-l-2 border-l-[color:var(--mr-warn)]" data-testid={`activity-flag-${f.id || ''}`}>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Fire size={12} className="text-[color:var(--mr-warn)]" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--mr-warn)]">Clara flag · {f.tone}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    <p className="text-xs text-foreground">{f.reason || f.summary || ''}</p>
                  </div>
                ))}
                {!data.escalations?.length && !data.flags?.length && <p className="text-xs text-muted-foreground text-center py-6">No activity yet.</p>}
              </div>
            )}

            {tab === 'notes' && (
              <div className="space-y-2">
                <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)} placeholder="Add a note… (saves as you type)"
                  data-testid="note-input" style={{ fontSize: '16px' }} rows={3}
                  className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm" />
                <div className="flex items-center gap-2">
                  <Button onClick={handleAddNote} disabled={!noteDraft.trim() || saving} className="mr-btn-primary h-9 px-4" data-testid="save-note">{saving ? 'Saving…' : 'Add note'}</Button>
                  {noteDraft && <span className="text-[10px] text-muted-foreground">Draft saved</span>}
                </div>
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Previous notes</p>
                  <pre className="whitespace-pre-wrap text-xs text-foreground font-sans bg-muted/30 p-2.5 rounded-lg border border-border">{file.notes || 'No notes yet.'}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/60 last:border-0">
      <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex-shrink-0">{k}</span>
      <span className="text-xs text-foreground text-right">{v || '—'}</span>
    </div>
  );
}

function ActionBtn({ onClick, href, icon: Icon, label, testid, disabled }) {
  const inner = (
    <div className={`flex flex-col items-center justify-center min-h-[52px] rounded-lg py-2 ${disabled ? 'opacity-40' : 'active:bg-muted/60'}`}>
      <Icon size={18} className="text-[color:var(--mr-blue-strong)]" />
      <span className="text-[10px] font-bold text-foreground mt-0.5">{label}</span>
    </div>
  );
  if (disabled) return <div data-testid={testid}>{inner}</div>;
  if (href) return <a href={href} data-testid={testid}>{inner}</a>;
  return <button onClick={onClick} data-testid={testid} className="w-full">{inner}</button>;
}
